import React from 'react';
import { MessageCircle, Instagram, Send, Mail } from 'lucide-react';

const Section = ({ icon: Icon, color, title, children }) => (
  <div className="contact-editor-channel" data-testid={`contact-channel-${title.toLowerCase()}`}>
    <div className="contact-editor-channel-head" style={{ color }}>
      <Icon size={18} />
      <span>{title}</span>
    </div>
    <div className="contact-editor-channel-body">{children}</div>
  </div>
);

const ContactEditor = ({
  whatsappNumber = '',
  whatsappMessage = '',
  whatsappLabel = 'WhatsApp',
  whatsappEnabled = false,
  instagramUsername = '',
  instagramEnabled = false,
  telegramUsername = '',
  telegramEnabled = false,
  emailAddress = '',
  emailSubject = '',
  emailEnabled = false,
  onChange,
}) => {
  const update = (patch) => onChange && onChange(patch);
  const cleanDigits = String(whatsappNumber || '').replace(/[^\d]/g, '');
  const waHref = cleanDigits
    ? `https://wa.me/${cleanDigits}${whatsappMessage ? `?text=${encodeURIComponent(whatsappMessage)}` : ''}`
    : null;
  const igUser = String(instagramUsername || '').trim().replace(/^@/, '');
  const igHref = igUser ? `https://instagram.com/${igUser}` : null;
  const tgUser = String(telegramUsername || '').trim().replace(/^@/, '');
  const tgHref = tgUser ? `https://t.me/${tgUser}` : null;
  const emHref = emailAddress
    ? `mailto:${emailAddress}${emailSubject ? `?subject=${encodeURIComponent(emailSubject)}` : ''}`
    : null;

  return (
    <div className="admin-section" data-testid="contact-editor-section">
      <h3 className="admin-section-title flex items-center gap-2">
        <MessageCircle size={18} className="text-green-400" /> Əlaqə kanalları
      </h3>
      <p className="admin-section-desc">
        Sağ-alt küncdə üzən kanal düymələri. Birdən çox aktiv olsa, əsas (WhatsApp varsa, o) görünür, qalanları
        "+N" düyməsinə klikləndikdə açılır.
      </p>

      <div className="space-y-3">
        {/* WhatsApp */}
        <Section icon={MessageCircle} color="#22c55e" title="WhatsApp">
          <label className="flex items-center gap-2 mb-2">
            <input type="checkbox" checked={!!whatsappEnabled} onChange={(e) => update({ whatsapp_enabled: e.target.checked })} data-testid="whatsapp-enabled-toggle" />
            <span className="text-sm text-white/85">Aktiv</span>
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <input type="text" className="karma-input text-left w-full" value={whatsappNumber}
              onChange={(e) => update({ whatsapp_number: e.target.value })}
              placeholder="Nömrə (ölkə kodu ilə): 994501234567" maxLength={24}
              data-testid="whatsapp-number-input" />
            <input type="text" className="karma-input text-left w-full" value={whatsappLabel}
              onChange={(e) => update({ whatsapp_label: e.target.value })}
              placeholder="Etiket: WhatsApp" maxLength={60}
              data-testid="whatsapp-label-input" />
          </div>
          <textarea className="karma-input text-left w-full mt-2" value={whatsappMessage}
            onChange={(e) => update({ whatsapp_message: e.target.value })}
            placeholder="İlkin mesaj (ixtiyari)..." maxLength={500} rows={2}
            data-testid="whatsapp-message-input"
            style={{ resize: 'vertical', minHeight: 50, fontFamily: 'inherit' }} />
          {waHref && (
            <a href={waHref} target="_blank" rel="noopener noreferrer" className="text-xs text-green-300 underline mt-2 inline-block break-all" data-testid="whatsapp-preview-link">{waHref}</a>
          )}
        </Section>

        {/* Instagram */}
        <Section icon={Instagram} color="#dd2a7b" title="Instagram">
          <label className="flex items-center gap-2 mb-2">
            <input type="checkbox" checked={!!instagramEnabled} onChange={(e) => update({ instagram_enabled: e.target.checked })} data-testid="instagram-enabled-toggle" />
            <span className="text-sm text-white/85">Aktiv</span>
          </label>
          <input type="text" className="karma-input text-left w-full" value={instagramUsername}
            onChange={(e) => update({ instagram_username: e.target.value })}
            placeholder="@karmaakademi (@ ixtiyaridir)" maxLength={60}
            data-testid="instagram-username-input" />
          {igHref && (
            <a href={igHref} target="_blank" rel="noopener noreferrer" className="text-xs text-pink-300 underline mt-2 inline-block break-all" data-testid="instagram-preview-link">{igHref}</a>
          )}
        </Section>

        {/* Telegram */}
        <Section icon={Send} color="#229ED9" title="Telegram">
          <label className="flex items-center gap-2 mb-2">
            <input type="checkbox" checked={!!telegramEnabled} onChange={(e) => update({ telegram_enabled: e.target.checked })} data-testid="telegram-enabled-toggle" />
            <span className="text-sm text-white/85">Aktiv</span>
          </label>
          <input type="text" className="karma-input text-left w-full" value={telegramUsername}
            onChange={(e) => update({ telegram_username: e.target.value })}
            placeholder="@karmaakademi və ya kanal ID" maxLength={60}
            data-testid="telegram-username-input" />
          {tgHref && (
            <a href={tgHref} target="_blank" rel="noopener noreferrer" className="text-xs text-sky-300 underline mt-2 inline-block break-all" data-testid="telegram-preview-link">{tgHref}</a>
          )}
        </Section>

        {/* Email */}
        <Section icon={Mail} color="#818cf8" title="Email">
          <label className="flex items-center gap-2 mb-2">
            <input type="checkbox" checked={!!emailEnabled} onChange={(e) => update({ email_enabled: e.target.checked })} data-testid="email-enabled-toggle" />
            <span className="text-sm text-white/85">Aktiv</span>
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <input type="email" className="karma-input text-left w-full" value={emailAddress}
              onChange={(e) => update({ email_address: e.target.value })}
              placeholder="info@karmaakademi.az" maxLength={120}
              data-testid="email-address-input" />
            <input type="text" className="karma-input text-left w-full" value={emailSubject}
              onChange={(e) => update({ email_subject: e.target.value })}
              placeholder="Mövzu (ixtiyari)" maxLength={200}
              data-testid="email-subject-input" />
          </div>
          {emHref && (
            <a href={emHref} className="text-xs text-indigo-300 underline mt-2 inline-block break-all" data-testid="email-preview-link">{emHref}</a>
          )}
        </Section>
      </div>
    </div>
  );
};

export default ContactEditor;
